import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { fa, formatDate, todayIso } from "../../core/format";
import Modal from "../../components/Modal";
import JalaliDate from "../../components/JalaliDate";
import ResponsiveList from "../../components/ResponsiveList";
import FileViewer from "../../components/FileViewer";
import MultiFileField from "../../components/MultiFileField";
import PrintReport from "../../components/PrintReport";
import { directivePrint } from "../../core/printConfigs";

const ST = {
  pending: ["در انتظار", "wait"],
  in_progress: ["در حال انجام", "warn"],
  done: ["انجام شد", "ok"],
  cancelled: ["غیرفعال", "bad"],
};
const Badge = ({ s }) => <span className={`badge ${ST[s][1]}`}>{ST[s][0]}</span>;

const sizeText = (b) =>
  b >= 1048576
    ? `${fa((b / 1048576).toFixed(1))} مگابایت`
    : `${fa(Math.max(1, Math.round(b / 1024)))} کیلوبایت`;
const fileIcon = (m) => (m === "application/pdf" ? "fa-regular fa-file-pdf" : "fa-regular fa-image");

async function uploadFiles(id, category, files) {
  if (!files.length) return;
  const fd = new FormData();
  fd.append("category", category);
  files.forEach((f) => fd.append("files", f));
  await api.post(`/directives/${id}/files`, fd, { timeout: 180000 });
}

/* گیرنده‌ها: «شیفت کاری: A و B» */
const namesOf = (r) => r.recipients.map((x) => x.name).join(" و ");
const targetLabel = (r) => `${r.targetType === "shift" ? "شیفت کاری" : "کاربر خاص"}: ${namesOf(r)}`;

/* اگر کسی انجام داده باشد فقط همان‌ها نمایش داده می‌شوند */
const shownRecipients = (r) => {
  const done = r.recipients.filter((x) => x.status === "done");
  return done.length ? done : r.recipients.filter((x) => x.status !== "cancelled");
};
const doneReports = (r) => r.recipients.filter((x) => x.status === "done");

const statusChips = (r) => (
  <div className="chips">
    {shownRecipients(r).map((x) => (
      <span key={x.id} className={`badge ${ST[x.status][1]}`}>
        {x.status === "done" ? `انجام شد توسط ${x.name}` : `${x.name}: ${ST[x.status][0]}${x.overdue ? " (مهلت گذشته)" : ""}`}
      </span>
    ))}
  </div>
);

const reportPreview = (r) => {
  const d = doneReports(r).find((x) => x.report);
  return d ? <div className="clip" title={d.report}>{d.report}</div> : "—";
};

/* ---------- جزئیات + گزارش + فایل‌ها ---------- */
function DetailModal({ row, isManager, onClose }) {
  const { toast, confirm } = useApp();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null);
  const [adding, setAdding] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/directives/${row.id}/files`);
      setFiles(data.items);
    } catch (e) {
      toast(errMsg(e), "err");
    } finally {
      setLoading(false);
    }
  }, [row.id, toast]);
  useEffect(() => { load(); }, [load]);

  const view = (f) => setViewer({ path: `/directives/${row.id}/files/${f.id}`, name: f.name });
  const remove = async (f) => {
    if (!(await confirm(`فایل «${f.name}» حذف شود؟`))) return;
    try { await api.delete(`/directives/${row.id}/files/${f.id}`); load(); } catch (e) { toast(errMsg(e), "err"); }
  };
  const addMore = async () => {
    if (!adding.length) return;
    setBusy(true);
    try { await uploadFiles(row.id, "directive", adding); setAdding([]); toast("فایل‌ها اضافه شد"); load(); }
    catch (e) { toast(errMsg(e), "err"); } finally { setBusy(false); }
  };

  const dirFiles = files.filter((f) => f.category === "directive");
  const repFiles = files.filter((f) => f.category === "report");

  const FileRow = ({ f, del }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", background: "var(--card)" }}>
      <i className={fileIcon(f.mime)} style={{ color: "var(--primary)" }} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{f.name}</span>
      {f.category === "report" && f.uploaderName && <small className="hint" style={{ margin: 0 }}>{f.uploaderName}</small>}
      <small className="hint" style={{ margin: 0 }}>{sizeText(f.size)}</small>
      <button className="icon-btn" title="مشاهده" onClick={() => view(f)}><i className="fa-regular fa-eye" /></button>
      {del && <button className="icon-btn danger" title="حذف" onClick={() => remove(f)}><i className="fa-solid fa-trash" /></button>}
    </div>
  );

  const box = { whiteSpace: "pre-wrap", lineHeight: 2, background: "var(--card-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px" };

  return (
    <>
      <Modal wide title={row.subject} onClose={onClose} footer={<button className="btn btn-ghost" onClick={onClose}>بستن</button>}>
        <div className="grid2">
          <div className="field"><label>مهلت انجام</label><div>{formatDate(row.deadline)}</div></div>
          {row.recipients && <div className="field"><label>گیرنده</label><div>{targetLabel(row)}</div></div>}
        </div>
        <div className="field"><label>شرح ابلاغ</label><div style={box}>{row.description}</div></div>

        {isManager && row.recipients && (
          <div className="field">
            <label>وضعیت و گزارش انجام</label>
            {doneReports(row).length === 0 ? (
              <div className="hint">هنوز انجام نشده است. {statusChips(row)}</div>
            ) : (
              doneReports(row).map((x) => (
                <div key={x.id} style={{ marginBottom: 10 }}>
                  <span className="badge ok" style={{ marginBottom: 6 }}>انجام شد توسط {x.name}</span>
                  <div style={box}>{x.report || <span className="hint">گزارشی ثبت نشده است.</span>}</div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="field">
          <label>پیوست‌های ابلاغ</label>
          {loading ? <div className="spinner sm" /> : (
            <div style={{ display: "grid", gap: 8 }}>
              {row.hasFile && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px" }}>
                  <i className="fa-solid fa-paperclip" style={{ color: "var(--primary)" }} />
                  <span style={{ flex: 1 }}>پیوست اصلی</span>
                  <button className="icon-btn" onClick={() => setViewer({ path: `/directives/${row.id}/file`, name: row.subject })}><i className="fa-regular fa-eye" /></button>
                </div>
              )}
              {dirFiles.map((f) => <FileRow key={f.id} f={f} del={isManager} />)}
              {!row.hasFile && dirFiles.length === 0 && <div className="hint">فایلی پیوست نشده است.</div>}
            </div>
          )}
          {isManager && (
            <div style={{ marginTop: 10 }}>
              <MultiFileField label="افزودن فایل" files={adding} onChange={setAdding} />
              {adding.length > 0 && <button className="btn btn-sm" onClick={addMore} disabled={busy}>{busy ? "در حال ارسال..." : "ثبت فایل‌ها"}</button>}
            </div>
          )}
        </div>

        {repFiles.length > 0 && (
          <div className="field">
            <label>فایل‌های گزارش انجام</label>
            <div style={{ display: "grid", gap: 8 }}>{repFiles.map((f) => <FileRow key={f.id} f={f} />)}</div>
          </div>
        )}
      </Modal>
      {viewer && <FileViewer {...viewer} onClose={() => setViewer(null)} />}
    </>
  );
}

/* ---------- ویرایش ابلاغ ---------- */
function EditModal({ row, onClose, onDone }) {
  const { toast } = useApp();
  const [form, setForm] = useState({
    subject: row.subject || "",
    description: row.description || "",
    deadline: row.deadline || todayIso(),
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.subject.trim()) return setErr("موضوع را وارد کنید");
    if (!form.description.trim()) return setErr("شرح را وارد کنید");
    if (!form.deadline) return setErr("مهلت انجام را انتخاب کنید");
    setBusy(true); setErr("");
    try {
      await api.put(`/directives/${row.id}`, {
        subject: form.subject.trim(),
        description: form.description.trim(),
        deadline: form.deadline,
      });
      toast("ابلاغ ویرایش شد");
      onDone();
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };

  return (
    <Modal title="ویرایش ابلاغ" onClose={() => !busy && onClose()}
      footer={<><button className="btn" onClick={submit} disabled={busy}>{busy ? "در حال ذخیره..." : "ذخیره تغییرات"}</button>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>انصراف</button></>}>
      {err && <div className="alert err">{err}</div>}
      <div className="field"><label>موضوع<span className="req">*</span></label>
        <input value={form.subject} maxLength={250} onChange={(e) => set("subject", e.target.value)} /></div>
      <div className="field"><label>شرح<span className="req">*</span></label>
        <textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
      <JalaliDate label={<>مهلت انجام<span className="req">*</span></>} value={form.deadline} onChange={(v) => set("deadline", v)} />
      <div className="hint">گیرنده‌ها بعد از ارسال قابل تغییر نیستند. برای افزودن یا حذف فایل، از دکمه‌ی «جزئیات» استفاده کنید.</div>
    </Modal>
  );
}

/* ---------- ساخت ابلاغ ---------- */
function CreateModal({ onClose, onDone }) {
  const { toast } = useApp();
  const [form, setForm] = useState({ subject: "", description: "", deadline: todayIso(), targetType: "shift" });
  const [files, setFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/employees").then((r) => setUsers(r.data.items.filter((e) => e.userId))).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.subject.trim()) return setErr("موضوع را وارد کنید");
    if (!form.description.trim()) return setErr("شرح را وارد کنید");
    if (form.targetType === "user" && !sel.length) return setErr("حداقل یک کاربر را انتخاب کنید");
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (form.targetType === "user") fd.append("userIds", JSON.stringify(sel));
      const { data } = await api.post("/directives", fd);

      if (files.length) {
        try {
          let id = data.id ?? data.item?.id ?? data.directiveId;
          if (!id) {
            const l = await api.get("/directives");
            id = l.data.items
              .filter((x) => String(x.subject).trim() === form.subject.trim())
              .sort((a, b) => b.id - a.id)[0]?.id;
          }
          await uploadFiles(id, "directive", files);
        } catch {
          toast("ابلاغ ثبت شد، اما بارگذاری فایل‌ها ناموفق بود. از جزئیات ابلاغ دوباره اضافه کنید.", "err");
        }
      }
      toast(`ابلاغ برای ${fa(data.recipients)} نفر ارسال شد`);
      onDone();
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };

  return (
    <Modal title="ابلاغ جدید" onClose={() => !busy && onClose()}
      footer={<><button className="btn" onClick={submit} disabled={busy}>{busy ? "در حال ارسال..." : "ارسال ابلاغ"}</button>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>انصراف</button></>}>
      {err && <div className="alert err">{err}</div>}
      <div className="field"><label>موضوع<span className="req">*</span></label>
        <input value={form.subject} maxLength={250} onChange={(e) => set("subject", e.target.value)} /></div>
      <div className="field"><label>شرح<span className="req">*</span></label>
        <textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
      <JalaliDate label={<>مهلت انجام<span className="req">*</span></>} value={form.deadline} onChange={(v) => set("deadline", v)} />
      <MultiFileField label="فایل‌های پیوست (اختیاری)" files={files} onChange={setFiles} />
      <div className="field"><label>ارسال برای</label>
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button type="button" className={`tab-btn ${form.targetType === "shift" ? "on" : ""}`} onClick={() => set("targetType", "shift")}>کاربرانِ شیفتِ امروز</button>
          <button type="button" className={`tab-btn ${form.targetType === "user" ? "on" : ""}`} onClick={() => set("targetType", "user")}>کاربر خاص</button>
        </div>
        {form.targetType === "shift" && <div className="hint">هنگام ثبت، افراد شیفتِ امروز تعیین می‌شوند؛ با انجام توسط یک نفر، برای بقیه غیرفعال می‌شود.</div>}
      </div>
      {form.targetType === "user" && (
        <div className="check-list">
          {users.map((u) => (
            <label key={u.userId} className={`check-item ${sel.includes(u.userId) ? "on" : ""}`} style={{ margin: 0 }}>
              <input type="checkbox" checked={sel.includes(u.userId)}
                onChange={() => setSel((s) => (s.includes(u.userId) ? s.filter((x) => x !== u.userId) : [...s, u.userId]))} />
              {u.firstName} {u.lastName}
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ---------- نمای مدیر بندر ---------- */
function ManagerView() {
  const { toast, confirm } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [create, setCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(null);
const [printRow, setPrintRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/directives")).data.items); } catch (e) { toast(errMsg(e), "err"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!(await confirm("این ابلاغ حذف شود؟"))) return;
    try { await api.delete(`/directives/${r.id}`); load(); } catch (e) { toast(errMsg(e), "err"); }
  };

  return (
    <>
      <div className="page-head">
        <div><h2>ابلاغ مدیر بندر</h2><p>ابلاغ به کاربر خاص یا شیفتِ امروز و پیگیری انجام</p></div>
        <button className="btn" onClick={() => setCreate(true)}><i className="fa-solid fa-plus" /> ابلاغ جدید</button>
      </div>
      <ResponsiveList loading={loading} rows={rows} empty="ابلاغی ثبت نشده است"
        columns={[
          { key: "createAt", label: "تاریخ", render: (r) => formatDate(String(r.createAt).slice(0, 10)) },
          { key: "subject", label: "موضوع", render: (r) => <b>{r.subject}</b> },
          { key: "deadline", label: "مهلت", render: (r) => formatDate(r.deadline) },
          { key: "target", label: "گیرنده", render: (r) => targetLabel(r) },
          { key: "recipients", label: "وضعیت", render: statusChips },
          { key: "report", label: "گزارش انجام", render: reportPreview },
        ]}
        mobileTitle={(r) => r.subject}
        mobileBadge={(r) => <span className="mc-date">مهلت: {formatDate(r.deadline)}</span>}
        mobileMeta={(r) => [
          { icon: "fa-solid fa-users", full: true, value: targetLabel(r) },
          { icon: "fa-solid fa-list-check", full: true, value: statusChips(r) },
          ...(doneReports(r).some((x) => x.report)
            ? [{ icon: "fa-regular fa-note-sticky", full: true, label: "گزارش", value: reportPreview(r) }]
            : []),
        ]}
        actions={(r) => (
          <>
            <button className="icon-btn" title="چاپ گزارش" onClick={() => setPrintRow(r)}><i className="fa-solid fa-print" /></button>
            <button className="icon-btn" title="جزئیات، گزارش و فایل‌ها" onClick={() => setDetail(r)}><i className="fa-regular fa-eye" /></button>

            <button className="icon-btn" title="ویرایش" onClick={() => setEdit(r)}><i className="fa-solid fa-pen" /></button>
            <button className="icon-btn danger" title="حذف" onClick={() => remove(r)}><i className="fa-solid fa-trash" /></button>
          </>
        )} />
      {create && <CreateModal onClose={() => setCreate(false)} onDone={() => { setCreate(false); load(); }} />}
      {edit && <EditModal row={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
      {detail && <DetailModal row={detail} isManager onClose={() => { setDetail(null); load(); }} />}
        {printRow && <PrintReport {...directivePrint(printRow)} onClose={() => setPrintRow(null)} />}
    </>
  );
}

/* ---------- ثبت انجام (چندفایله) ---------- */
function FinishModal({ item, onClose, onDone }) {
  const { toast } = useApp();
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (text.trim().length < 10) return setErr("حداقل ۱۰ کاراکتر بنویسید");
    setBusy(true); setErr("");
    try {
      await api.post(`/directives/${item.id}/complete`, { report: text.trim() });
    } catch (e) { setErr(errMsg(e)); setBusy(false); return; }
    if (files.length) {
      try { await uploadFiles(item.id, "report", files); }
      catch { toast("ابلاغ ثبت شد، اما بارگذاری فایل‌ها ناموفق بود", "err"); onDone(); return; }
    }
    toast("ابلاغ ثبت شد");
    onDone();
  };

  return (
    <Modal title="ثبت انجام ابلاغ" onClose={() => !busy && onClose()}
      footer={<><button className="btn" onClick={go} disabled={busy}>{busy ? "در حال ثبت..." : "ثبت و انجام شد"}</button>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>انصراف</button></>}>
      {err && <div className="alert err">{err}</div>}
      <b>{item.subject}</b>
      <div className="field" style={{ marginTop: 10 }}><label>شرح اقدامات<span className="req">*</span></label>
        <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} /></div>
      <MultiFileField label="فایل‌های پیوست (اختیاری)" files={files} onChange={setFiles} />
    </Modal>
  );
}

/* ---------- نمای کاربر ---------- */
function MyView() {
  const { toast } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fin, setFin] = useState(null);
  const [detail, setDetail] = useState(null);
  const [printRow, setPrintRow] = useState(null);


  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/directives")).data.items); } catch (e) { toast(errMsg(e), "err"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const start = async (r) => {
    try { await api.post(`/directives/${r.id}/start`, {}); toast("ابلاغ شروع شد"); load(); } catch (e) { toast(errMsg(e), "err"); load(); }
  };
  const status = (r) => (
    <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
      <Badge s={r.myStatus} />
      {r.overdue && <span className="badge bad">مهلت گذشته</span>}
      {r.myStatus === "cancelled" && r.doneByName && <small className="hint">انجام توسط {r.doneByName}</small>}
    </div>
  );

  return (
    <>
      <div className="page-head"><div><h2>ابلاغ مدیر بندر</h2><p>ابلاغ‌هایی که برای شما ارسال شده است</p></div></div>
      <ResponsiveList loading={loading} rows={rows} empty="ابلاغی برای شما ثبت نشده است"
        columns={[
          { key: "subject", label: "موضوع", render: (r) => <div><b>{r.subject}</b><div className="hint">{r.description}</div></div> },
          { key: "deadline", label: "مهلت", render: (r) => formatDate(r.deadline) },
          { key: "myStatus", label: "وضعیت", render: status },
        ]}
        mobileTitle={(r) => r.subject}
        mobileBadge={status}
        mobileMeta={(r) => [
          { icon: "fa-regular fa-calendar", label: "مهلت", value: formatDate(r.deadline) },
          { icon: "fa-regular fa-note-sticky", full: true, value: r.description },
        ]}
        actions={(r) => (
          <>
          <button className="icon-btn" title="چاپ گزارش" onClick={() => setPrintRow(r)}><i className="fa-solid fa-print" /></button>

            <button className="icon-btn" title="جزئیات و فایل‌ها" onClick={() => setDetail(r)}><i className="fa-solid fa-folder-open" /></button>
            {r.myStatus === "pending" && <button className="btn btn-sm btn-ghost" onClick={() => start(r)}>شروع</button>}
            {["pending", "in_progress"].includes(r.myStatus) && <button className="btn btn-sm" onClick={() => setFin(r)}><i className="fa-solid fa-check" /> انجام شد</button>}
          </>
        )} />
      {fin && <FinishModal item={fin} onClose={() => setFin(null)} onDone={() => { setFin(null); load(); }} />}
      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
        {printRow && <PrintReport {...directivePrint(printRow)} onClose={() => setPrintRow(null)} />}
    </>
  );
}

export default function Directives() {
  const { user } = useApp();
  return user.role === "manager" ? <ManagerView /> : <MyView />;
}